import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth, homeFor, Spinner } from "@/lib/auth";
import { api, errMsg, fmtDate } from "@/lib/api";
import { StatusBadge, Card, SectionTitle, Timeline, Gallery, Messages } from "@/components/common";
import CustomerPanel from "@/components/CustomerPanel";
import ContractorPanel from "@/components/ContractorPanel";
import OwnerPanel from "@/components/OwnerPanel";
import { CleaningCard } from "@/components/CleaningCard";
import { mapsLink } from "@/components/LocateMe";

function Row({ label, value }) {
  if (!value) return null;
  return <div><dt className="text-xs text-slate-500">{label}</dt><dd className="text-sm text-slate-900 whitespace-pre-wrap">{value}</dd></div>;
}

function Details({ r, role }) {
  const { t } = useI18n();
  return (
    <Card data-testid="request-details">
      <SectionTitle>{t("d.details")}</SectionTitle>
      <dl className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Row label={t("req.what")} value={r.description} /></div>
        <Row label={t("req.when")} value={r.started} />
        <Row label={t("req.hazard_q")} value={r.hazards?.map((h) => t(`hz.${h}`)).join(", ")} />
        <Row label={t("req.danger_q")} value={t(r.danger ? "yes" : "no")} />
        <Row label={t("req.safe_q")} value={t(r.safe_to_remain ? "yes" : "no")} />
        <Row label={t("d.location")} value={[r.city, r.neighborhood].filter(Boolean).join(", ")} />
        <Row label={t("req.directions")} value={r.directions} />
        <Row label={t("req.access")} value={r.access} />
        {r.geo && <div><dt className="text-xs text-slate-500">{t("loc.label")}</dt><dd><a href={mapsLink(r.geo)} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline" data-testid="request-maps-link">{t("loc.map")} ↗</a></dd></div>}
        <Row label={t("d.availability")} value={`${r.preferred_times || "—"}${r.available_24h ? ` · ${t("req.avail24")}` : ""}`} />
        {role !== "customer" && r.contact_phone && <Row label={t("d.contact")} value={`${r.contact_name} · ${r.contact_phone} · ${t(`m.${r.contact_method}`)}`} />}
        <Row label={t("d.completion")} value={r.completion_notes} />
        <Row label={t("d.concern")} value={r.concern} />
      </dl>
    </Card>
  );
}

function Uploader({ r, role, onDone }) {
  const { t } = useI18n();
  const [stage, setStage] = useState(role === "customer" ? "request" : "before");
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const files = [...e.target.files];
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("stage", stage);
      await api.post(`/requests/${r.id}/files`, fd).catch((err) => toast.error(errMsg(err)));
    }
    setBusy(false);
    e.target.value = "";
    onDone();
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      {role !== "customer" && (
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="upload-stage-select">
          {["before", "during", "after"].map((s) => <option key={s} value={s}>{t(`st.${s}`)}</option>)}
        </select>
      )}
      <label className={`inline-flex items-center gap-2 h-9 px-4 rounded-full bg-slate-900 text-white text-sm cursor-pointer ${busy ? "opacity-50" : ""}`}>
        <Upload className="h-4 w-4" />{t("d.upload")}
        <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={up} disabled={busy} data-testid="upload-file-input" />
      </label>
    </div>
  );
}

function MessagesCard({ r, role, reload }) {
  const { t } = useI18n();
  if (role === "customer") return <Card><SectionTitle>{t("d.messages")}</SectionTitle><Messages request={r} channel="customer" onSent={reload} /></Card>;
  return (
    <Card>
      <SectionTitle>{t("d.messages")}</SectionTitle>
      <Tabs defaultValue={role === "contractor" ? "customer" : "internal"}>
        <TabsList><TabsTrigger value="internal" data-testid="msg-tab-internal">{t("o.msg_team")}</TabsTrigger><TabsTrigger value="customer" data-testid="msg-tab-customer">{t("o.msg_customer")}</TabsTrigger></TabsList>
        <TabsContent value="internal"><Messages request={r} channel="internal" onSent={reload} /></TabsContent>
        <TabsContent value="customer"><Messages request={r} channel="customer" onSent={reload} /></TabsContent>
      </Tabs>
    </Card>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/requests/${id}`).then((x) => setR(x.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const role = user.role;
  if (err) return <p className="max-w-3xl mx-auto py-24 text-center text-slate-500" data-testid="request-error">{err}</p>;
  if (!r) return <Spinner />;
  const Panel = { customer: CustomerPanel, contractor: ContractorPanel, owner: OwnerPanel }[role];
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="request-detail">
      <Link to={homeFor(role)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="request-back-link"><ArrowLeft className="h-4 w-4 rtl:rotate-180" />{t("d.back")}</Link>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="request-detail-number">{r.number}</h1>
        <StatusBadge status={r.status} />
        {r.urgent && <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2.5 py-0.5 text-xs font-semibold" data-testid="urgent-badge"><AlertTriangle className="h-3 w-3" />{t("d.urgent")}</span>}
      </div>
      <p className="text-slate-500 mt-1">{t(r.kind === "cleaning" ? "cl.label" : `svc.${r.service}`)} · {r.city} · <span className="font-mono text-xs">{fmtDate(r.created_at)}</span></p>
      {r.emergency_flag && <p className="mt-4 rounded-2xl bg-red-50 border-2 border-red-200 p-4 text-sm text-red-800">{t("req.emergency")}</p>}
      {r.group?.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" data-testid="request-group">
          <span className="text-slate-500">{t("d.group")}:</span>
          {r.group.map((g) => (
            <Link key={g.id} to={`${role === "owner" ? "/owner/requests" : "/dashboard/requests"}/${g.id}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 hover:border-teal-500" data-testid={`group-link-${g.number}`}>
              <span className="font-mono text-xs">{g.number}</span>{t(`svc.${g.service}`)}<StatusBadge status={g.status} />
            </Link>
          ))}
        </div>
      )}
      <div className="mt-8 grid lg:grid-cols-[1.5fr_1fr] gap-6 items-start">
        <div className="space-y-6">
          {r.kind === "cleaning" && r.cleaning && <CleaningCard r={r} role={role} reload={load} />}
          <Details r={r} role={role} />
          <Card><SectionTitle>{t("d.photos")}</SectionTitle><Gallery files={r.files} /><Uploader r={r} role={role} onDone={load} /></Card>
          <MessagesCard r={r} role={role} reload={load} />
        </div>
        <div className="space-y-6">
          <Panel r={r} reload={load} />
          <Card><SectionTitle>{t("d.history")}</SectionTitle><Timeline history={r.history} /></Card>
        </div>
      </div>
    </div>
  );
}
